import React from 'react';
import { Page, Text, View, Document, Font } from '@react-pdf/renderer';
import { ToWords } from 'to-words';
import moment from 'moment-timezone';
import { IntlShape } from 'react-intl';

import notoSansArabic from 'fonts/NotoSansArabic-Regular.ttf';
import RobotoBold from 'fonts/Roboto-Medium.ttf';
import RobotoRegular from 'fonts/Roboto-Regular.ttf';
import RobotoItalic from 'fonts/Roboto-Italic.ttf';
import RobotoBoldItalic from 'fonts/Roboto-BoldItalic.ttf';

import { translate } from 'containers/services/translate';
import messages from 'containers/TransactionManagerTimeLine/OfferRegulation/messages';
import { styles } from 'containers/TransactionManagerTimeLine/OfferRegulation/documents/styles';

Font.register({
  family: 'NotoSansArabic',
  src: notoSansArabic,
});

Font.register({
  family: 'Roboto',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 'bold' },
    { src: RobotoItalic, fontStyle: 'italic' },
    { src: RobotoBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

export interface ReceiptDocumentVictimProps {
  intl: IntlShape;
  body: any;
}

const toWords = new ToWords({ localeCode: 'fr-FR' });

const currencyOptions = {
  currency: true,
  currencyOptions: {
    name: 'dirham',
    plural: 'dirhams',
    symbol: 'DH',
    fractionalUnit: {
      name: 'centime',
      plural: 'centimes',
      symbol: '',
    },
  },
};

const amountToWords = (amount: number) =>
  toWords.convert(amount, currencyOptions);

export const ReceiptDocumentVictim: React.FC<ReceiptDocumentVictimProps> = ({
  intl,
  body,
}) => {
  const secondParagraphMessage = body.isMinor
    ? messages.ReceiptDocumentMinorSecondParagraph
    : messages.receiptMajorVictimSecondParagraph;

  const secondParagraphValues = {
    fullNameVictim: body.isMinor ? body.tutorFullName : body.victimName,
    victimCin: body.isMinor ? body.tutorCin : body.victimCin,
    lossDate: body.lossDate,
    amount: body.amount,
    amountByLetter: amountToWords(body.amount),
    partage: body.responsabilityRate,
    fullNameDoctor: body.fullNameDoctor,
  };

  const prejudiceTable = [
    {
      label: messages.rateIpp,
      nature: '',
      rate: body.rateIPP,
      amount: body.ipp,
    },
    {
      label: messages.painDamage,
      nature: '',
      rate: body.ratePretium,
      amount: body.pretium,
    },
    {
      label: messages.aestheticDamageWithoutConseq,
      nature: body.aestheticDamageWithoutConsequenceNature,
      rate: body.aestheticDamageWithoutConsequenceRate,
      amount: body.aestheticDamageWithoutConsequenceAmount,
    },
    {
      label: messages.aestheticDamageWithConseq,
      nature: body.aestheticDamageWithConsequenceNature,
      rate: body.aestheticDamageWithConsequenceRate,
      amount: body.aestheticDamageWithConsequenceAmount,
    },
    {
      label: messages.useThirdParty,
      nature: body.rateRecoveryNature,
      rate: body.rateRecovery,
      amount: body.rateRecoveryAmount,
    },
    {
      label: messages.careerDamage,
      nature: body.careerPrejudiceNature,
      rate: body.careerPrejudiceRate,
      amount: body.careerPrejudiceAmount,
    },
    {
      label: messages.educationDamage,
      nature: body.schoolingPrejudiceNature,
      rate: body.schoolingPrejudiceRate,
      amount: body.schoolingPrejudiceAmount,
    },
    {
      label: messages.materialDamage,
      nature: '',
      rate: '',
      amount: '',
    },
    {
      label: messages.medicalFees,
      nature: '',
      rate: '',
      amount: body.medicalPharmaceuticalFees,
    },
  ];
  const victimInfoRow = [
    {
      message: messages.birthDateBox,
      value: { victimBirthDate: body.victimBirthDate },
    },
    {
      message: messages.cinBox,
      value: { victimCin: body.victimCin },
    },
    {
      message: messages.refBox,
      value: { reference: body.referenceCapital },
    },
  ];

  const dotMessages = [
    messages.firstDotText,
    messages.secondDotText,
    messages.thirdDotText,
    messages.forthDotText,
    messages.fifthDotText,
    messages.sixthDotText,
  ];

  return (
    <Document>
      <Page style={styles.page2}>
        <Text style={styles.headerText}>
          {translate(messages.receiptMajorVictimTitle, { intl })}
        </Text>

        <View style={styles.section}>
          <Text style={styles.textCenter}>
            {translate(
              messages.receiptMajorVictimSubTitle,
              { intl },
              {
                claimNumber: body.claimNumber,
              },
            )}
          </Text>
        </View>

        <View style={styles.sectionBetween}>
          <Text>
            {translate(messages.receiptMajorVictimFirstParaph, { intl })}
          </Text>
        </View>

        <View style={styles.sectionBetween}>
          <Text>
            {translate(secondParagraphMessage, { intl }, secondParagraphValues)}
          </Text>
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.textBox}>
              <Text>
                {translate(
                  messages.victimBox,
                  { intl },
                  {
                    fullNameVictim: body.victimName,
                  },
                )}
              </Text>
            </View>

            <View style={styles.textBox}>
              <Text>
                {translate(
                  messages.revenuBox,
                  { intl },
                  {
                    victimRevenu: body.revenu,
                  },
                )}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.sectionBetween}>
          <View style={styles.row}>
            {victimInfoRow.map((item, index) => (
              <View key={index}>
                <View style={styles.textBoxSecond}>
                  <Text>{translate(item.message, { intl }, item.value)}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.sectionBetween}>
          <View style={styles.row}>
            <View>
              <View style={styles.textBoxThird}>
                <Text>{translate(messages.prejudicBox, { intl })}</Text>
              </View>
              {prejudiceTable.map((item, index) => (
                <View key={index} style={styles.textBoxThird}>
                  <Text>{translate(item.label, { intl })}</Text>
                </View>
              ))}
            </View>

            {/* NATURE COLUMN */}
            <View>
              <View style={styles.textBoxSmall}>
                <Text>{translate(messages.natureBox, { intl })}</Text>
              </View>
              {prejudiceTable.map((row, index) => (
                <View key={index} style={styles.textBoxSmall}>
                  <Text>{row.nature || ' '}</Text>
                </View>
              ))}
            </View>
            {/* RATE COLUMN */}
            <View>
              <View style={styles.textBoxSmall}>
                <Text>{translate(messages.rateBox, { intl })}</Text>
              </View>
              {prejudiceTable.map((row, index) => (
                <View key={index} style={styles.textBoxSmall}>
                  <Text>{row.rate || ' '}</Text>
                </View>
              ))}
            </View>
            {/* AMOUNT COLUMN */}
            <View>
              <View style={styles.textBoxSmall}>
                <Text>{translate(messages.amountBox, { intl })}</Text>
              </View>
              {prejudiceTable.map((row, index) => (
                <View key={index} style={styles.textBoxSmall}>
                  <Text>{row.amount || ' '}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={styles.sectionBetween}>
          <View style={styles.row}>
            <View style={styles.textBoxForth}>
              <Text>{translate(messages.totalAmount, { intl })}</Text>
            </View>
            <View style={styles.textBoxFifth}>
              <Text>{body.amount}</Text>
            </View>
          </View>
        </View>
        <View style={styles.sectionBetween}>
          <Text>{translate(messages.thirdParagraph, { intl })}</Text>

          {dotMessages.map((msg, index) => (
            <View key={index} style={styles.row}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.dotText}>{translate(msg, { intl })}</Text>
            </View>
          ))}
        </View>

        <View style={[styles.rowColumn, styles.sectionBetween]}>
          <View style={styles.finalsection2}>
            <Text>{translate(messages.ReceiptDocumentFooter3, { intl })}</Text>
          </View>

          <View style={styles.finalsection2}>
            <Text>
              {translate(
                messages.ReceiptDocumentFooter2,
                { intl },
                {
                  date: moment().format('DD/MM/YYYY'),
                },
              )}
            </Text>
          </View>
        </View>

        <View style={styles.sectionBetween}>
          <Text>{translate(messages.ReceiptDocumentFooter4, { intl })}</Text>
        </View>

        <View style={styles.sectionBetween}>
          <Text>{translate(messages.ReceiptDocumentFooter5, { intl })}</Text>
        </View>

        <View style={styles.finalsectionSignature}>
          <Text>{translate(messages.ReceiptDocumentFooter6, { intl })}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReceiptDocumentVictim;
